import { useState } from "react";

import type { Profile } from "../../types/profile";

interface ProfileLockDialogProps {
  profile: Profile;
  onUnlock: (password: string, rememberMe: boolean) => void;
  onCancel: () => void;
  error?: string;
}

export const ProfileLockDialog = ({
  profile,
  onUnlock,
  onCancel,
  error,
}: ProfileLockDialogProps) => {
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      onUnlock(password, rememberMe);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: "rgba(255, 255, 255, 0.95)",
          border: "1px solid #e0e0e0",
          padding: "2.5rem",
          borderRadius: "12px",
          minWidth: "350px",
          maxWidth: "450px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          color: "#1a1a1a",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          style={{
            margin: "0 0 1.5rem 0",
            fontSize: "1.5rem",
            fontWeight: 600,
            color: "#1a1a1a",
            letterSpacing: "-0.5px",
          }}
        >
          Enter password for {profile.name}
        </h2>
        <form onSubmit={handleSubmit}>
          <div
            style={{
              position: "relative",
              marginBottom: error ? "0.75rem" : "1.25rem",
            }}
          >
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoFocus
              disabled={isLoading}
              style={{
                width: "100%",
                padding: "0.875rem 2.5rem 0.875rem 0.875rem",
                border: "1.5px solid #d0d0d0",
                borderRadius: "6px",
                fontSize: "1rem",
                background: "#ffffff",
                color: "#1a1a1a",
                boxSizing: "border-box",
                opacity: isLoading ? 0.6 : 1,
                cursor: isLoading ? "not-allowed" : "text",
                transition: "border-color 0.2s",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = profile.color;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#d0d0d0";
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isLoading}
              style={{
                position: "absolute",
                right: "0.75rem",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: isLoading ? "not-allowed" : "pointer",
                color: "#999",
                fontSize: "1.1rem",
                padding: "0.25rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: isLoading ? 0.6 : 1,
                transition: "color 0.2s",
              }}
              onMouseEnter={(e) => {
                if (!isLoading) e.currentTarget.style.color = "#1a1a1a";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#999";
              }}
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? "👁️" : "👁️‍🗨️"}
            </button>
          </div>
          {error && (
            <div
              style={{
                background: "#ffe6e6",
                border: "1px solid #ff8080",
                color: "#d32f2f",
                padding: "0.75rem",
                borderRadius: "6px",
                marginBottom: "1.25rem",
                fontSize: "0.9rem",
                fontWeight: 500,
              }}
            >
              {error}
            </div>
          )}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              marginBottom: "1.75rem",
              cursor: isLoading ? "not-allowed" : "pointer",
              color: "#1a1a1a",
              fontSize: "0.95rem",
              userSelect: "none",
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={isLoading}
              style={{
                cursor: isLoading ? "not-allowed" : "pointer",
                width: "18px",
                height: "18px",
              }}
            />
            <span>Remember me for 30 days</span>
          </label>
          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              style={{
                padding: "0.625rem 1.25rem",
                border: "1.5px solid #d0d0d0",
                background: "transparent",
                color: "#1a1a1a",
                borderRadius: "6px",
                fontSize: "0.95rem",
                fontWeight: 500,
                cursor: isLoading ? "not-allowed" : "pointer",
                opacity: isLoading ? 0.6 : 1,
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.background = "#f5f5f5";
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.background = "transparent";
                }
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                padding: "0.625rem 1.25rem",
                border: "none",
                background: isLoading ? `${profile.color}dd` : profile.color,
                color: "white",
                borderRadius: "6px",
                fontSize: "0.95rem",
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                opacity: isLoading ? 0.8 : 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.625rem",
                transition: "all 0.2s",
                boxShadow: isLoading ? "none" : `0 4px 12px ${profile.color}40`,
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.boxShadow = `0 6px 16px ${profile.color}60`;
                  e.currentTarget.style.transform = "translateY(-1px)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.boxShadow = `0 4px 12px ${profile.color}40`;
                  e.currentTarget.style.transform = "translateY(0)";
                }
              }}
            >
              {isLoading ? (
                <>
                  <span
                    style={{
                      display: "inline-block",
                      width: "13px",
                      height: "13px",
                      border: "2px solid rgba(255, 255, 255, 0.3)",
                      borderTop: "2px solid white",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                  Verifying...
                </>
              ) : (
                "Unlock"
              )}
            </button>
          </div>
        </form>
      </div>
      <style>{`
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};
